import { useCallback, useEffect, useRef } from 'react';
import { STEP_SLUGS } from '../utils/funnelSteps';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';
const SESSION_KEY = 'myl_analytics_session';
const META_KEY = 'myl_session_meta';

function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // sessionStorage blocked (Safari private mode, quota exceeded)
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function getDevice(): string {
  return window.innerWidth < 768 ? 'mobile' : 'desktop';
}

function getPlatform(): string {
  const urlPlatform = new URLSearchParams(window.location.search).get('platform');
  if (urlPlatform === 'prestashop') return 'prestashop';
  if (window.location.hostname.includes('patitasco')) return 'prestashop';
  return 'shopify';
}

interface SessionMeta {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  referrer: string | null;
  landing_path: string | null;
}

function bootstrapMeta(): SessionMeta {
  try {
    const cached = sessionStorage.getItem(META_KEY);
    if (cached) {
      return JSON.parse(cached) as SessionMeta;
    }
  } catch {
    /* fall through to fresh capture */
  }

  const q = new URLSearchParams(window.location.search);
  const meta: SessionMeta = {
    utm_source: q.get('utm_source'),
    utm_medium: q.get('utm_medium'),
    utm_campaign: q.get('utm_campaign'),
    utm_content: q.get('utm_content'),
    utm_term: q.get('utm_term'),
    fbclid: q.get('fbclid'),
    gclid: q.get('gclid'),
    referrer: document.referrer || null,
    landing_path: window.location.pathname + window.location.search,
  };

  try {
    sessionStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    /* quota / blocked — non-fatal */
  }

  return meta;
}

function shallowDiff(prev: Record<string, unknown>, next: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(next || {})) {
    if (prev[k] !== v) out[k] = v;
  }
  return out;
}

interface AnalyticsEvent {
  session_id: string;
  event_type: string;
  step_number?: number;
  step_name?: string;
  metadata?: Record<string, unknown>;
  session_meta?: SessionMeta;
}

export function useAnalytics(dogProfile?: Record<string, unknown>) {
  const sessionId = useRef(getOrCreateSessionId());
  const viewedSteps = useRef(new Set<number>());
  const stepStartTime = useRef<number>(0);
  const bufferRef = useRef<AnalyticsEvent[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStepRef = useRef<number>(0);
  const metaSentRef = useRef<boolean>(false);
  const lastProfileRef = useRef<Record<string, unknown>>({});

  const flush = useCallback(() => {
    const events = bufferRef.current;
    if (events.length === 0) return;
    bufferRef.current = [];

    const body = JSON.stringify(events.length === 1 ? events[0] : { events });
    const endpoint = events.length === 1 ? '/analytics/event' : '/analytics/events';

    // Fire-and-forget
    fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  }, []);

  const enqueue = useCallback(
    (event: AnalyticsEvent) => {
      bufferRef.current.push(event);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, 150);
    },
    [flush],
  );

  const trackEvent = useCallback(
    (type: string, stepNumber?: number, metadata?: Record<string, unknown>) => {
      const baseMetadata: Record<string, unknown> = {
        device: getDevice(),
        referrer: document.referrer || undefined,
        viewport_width: window.innerWidth,
        ...metadata,
      };

      // Attach dog_profile_delta if the caller-supplied profile has changed
      if (dogProfile && typeof dogProfile === 'object') {
        const delta = shallowDiff(lastProfileRef.current, dogProfile);
        if (Object.keys(delta).length > 0) {
          baseMetadata.dog_profile_delta = delta;
          lastProfileRef.current = { ...lastProfileRef.current, ...delta };
        }
      }

      const event: AnalyticsEvent = {
        session_id: sessionId.current,
        event_type: type,
        step_number: stepNumber,
        step_name: stepNumber ? STEP_SLUGS[stepNumber] : undefined,
        metadata: baseMetadata,
      };

      // First-event meta bootstrap (UTMs + referrer + landing path)
      if (!metaSentRef.current) {
        event.session_meta = bootstrapMeta();
        metaSentRef.current = true;
      }

      enqueue(event);

      // Session lifecycle: on funnel_complete, clear the session so the next
      // attempt starts fresh. Flush synchronously so the terminal event is sent
      // under the current session_id before we rotate it.
      if (type === 'funnel_complete') {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        flush();
        try {
          sessionStorage.removeItem(SESSION_KEY);
          sessionStorage.removeItem(META_KEY);
        } catch {
          /* noop */
        }
        metaSentRef.current = false;
        lastProfileRef.current = {};
        sessionId.current =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
    },
    [dogProfile, enqueue, flush],
  );

  const trackStepChange = useCallback(
    (newStep: number) => {
      const prevStep = lastStepRef.current;

      // Complete previous step
      if (prevStep > 0 && stepStartTime.current > 0) {
        const duration_ms = Date.now() - stepStartTime.current;
        trackEvent('step_complete', prevStep, { duration_ms });
      }

      // View new step (deduplicated)
      if (!viewedSteps.current.has(newStep)) {
        viewedSteps.current.add(newStep);
        if (newStep === 1) {
          trackEvent('funnel_start', 1, { platform: getPlatform() });
        }
        trackEvent('step_view', newStep);
      }

      stepStartTime.current = Date.now();
      lastStepRef.current = newStep;
    },
    [trackEvent],
  );

  // beforeunload — best-effort abandon tracking
  useEffect(() => {
    const handleUnload = () => {
      if (lastStepRef.current > 0 && lastStepRef.current < 14) {
        const event: AnalyticsEvent = {
          session_id: sessionId.current,
          event_type: 'funnel_abandon',
          step_number: lastStepRef.current,
          step_name: STEP_SLUGS[lastStepRef.current],
          metadata: { device: getDevice() },
        };
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          navigator.sendBeacon(`${API_URL}/analytics/event`, JSON.stringify(event));
        }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  return { trackEvent, trackStepChange, sessionId: sessionId.current };
}
